import {RFQ_FAMILIES} from './rfqProductCatalog.mjs';

const canonical = RFQ_FAMILIES.flatMap(group => group.products.map(p => ({...p, category: group.category})));
const identityKey = /customer|client|contact|email|phone|address|company|solicitante|cliente|correo|telefono|direccion|empresa|notes|observaciones|attachment|adjunto/i;
const tidy = value => String(value ?? '').normalize('NFC').replace(/[\u0000-\u001f\u007f]/g, ' ').trim();
const spanishLabels = {
    "material": "Material",
    "size": "Dimensiones",
    "print": "Impresión",
    "production": "Producción solicitada",
    "flute": "Tipo de corrugado",
    "board_thickness": "Espesor del cartón",
    "strength": "Resistencia requerida",
    "finish": "Acabado",
    "box_style": "Estilo de caja",
    "label_type": "Presentación de etiquetas",
    "shape": "Forma",
    "unwind_direction": "Dirección de salida",
    "thickness": "Espesor requerido",
    "adhesive": "Adhesivo",
    "laminate": "Laminado",
    "white_ink": "Tinta blanca",
    "foil_color": "Color del foil",
    "perforation": "Perforación",
    "colors": "Colores de impresión",
    "zipper": "Cierre zipper",
    "tear_line": "Apertura fácil",
    "valve": "Válvula",
    "size_capacity": "Capacidad solicitada (indicar ml u oz)",
    "wall": "Pared",
    "lid_type": "Tipo de tapa",
    "lid_color": "Color de tapa",
    "color": "Color",
    "lid": "Tapa",
    "presentation": "Presentación / empaque",
    "application": "Aplicación prevista",
    "composition": "Composición requerida",
    "fat": "Grasa requerida (%)",
    "protein": "Proteína requerida (%)",
    "process": "Proceso solicitado",
    "roast": "Tueste solicitado",
    "format": "Grano / molido y molienda",
    "flavor": "Sabor solicitado",
    "processing": "Natural / alcalinizado",
    "ph": "pH requerido"
};
const label = key => spanishLabels[key] || tidy(key).replace(/[_-]+/g, ' ').replace(/^./, c => c.toUpperCase());
// Only this projection reaches the PDF. Never serialize the opportunity, notes, files or company.
export function specificationProducts(request, lines = [], block = 0) {
    if (request?.businessUnit !== 'empaque' || request.stage !== 'pricing' || !request.products?.length || request.products.length > 20) throw new Error('La solicitud de especificaciones no está disponible.');
    const customer = tidy(request.company?.name).toLocaleLowerCase();
    const safe = value => {
        const text = tidy(value);
        return (customer.length >= 3 && text.toLocaleLowerCase().includes(customer)) || /[\w.+-]+@[\w.-]+\.[a-z]{2,}/i.test(text) ? 'Por confirmar' : text;
    };
    return request.products.map((source, index) => {
        const product = canonical.find(p => p.salesProductId === source.productId);
        if (!product) throw new Error('Hay un producto sin identificar; confirma la solicitud de Ventas.');
        const edited = index >= block * 5 && index < block * 5 + 5 ? lines[index - block * 5] : null;
        const quantity = edited?.product ? Number(edited.quantity) : source.quantity;
        const specs = Object.entries(source.specifications || {}).filter(([key, value]) => !identityKey.test(key) && ['string', 'number', 'boolean'].includes(typeof value) && tidy(value)).map(([key, value]) => [safe(label(key)), safe(value)]);
        return {name: product.name, category: product.category,
            quantity: Number.isSafeInteger(quantity) && quantity > 0 ? String(quantity) : 'Por confirmar',
            unit: safe(edited?.product ? edited.unit : source.unit) || 'Por confirmar',
            type: ['Genérico', 'Personalizado'].includes(edited?.type) ? edited.type : '', specifications: specs};
    });
}

// A small, dependency-free A4 PDF writer. Streams use WinAnsi hex strings so customer
// input cannot inject PDF operators; unsupported glyphs retain their Unicode code point.
const extraAnsi = new Map([['€',128],['‚',130],['ƒ',131],['„',132],['…',133],['†',134],['‡',135],['ˆ',136],['‰',137],['Š',138],['‹',139],['Œ',140],['Ž',142],['‘',145],['’',146],['“',147],['”',148],['•',149],['–',45],['—',45],['˜',152],['™',153],['š',154],['›',155],['œ',156],['ž',158],['Ÿ',159]]);
const ansi = value => [...tidy(value)].map(c => c.codePointAt(0) >= 32 && c.codePointAt(0) <= 255 ? c : extraAnsi.has(c) ? String.fromCharCode(extraAnsi.get(c)) : `[U+${c.codePointAt(0).toString(16).toUpperCase()}]`).join('');
const hex = value => [...ansi(value)].map(c => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
const wrap = (value, max) => {
    const text = ansi(value), result = [];
    for (let rest = text; rest.length;) {
        let at = rest.length <= max ? rest.length : rest.lastIndexOf(' ', max);
        if (at <= 0) at = Math.min(max, rest.length);
        result.push(rest.slice(0, at)); rest = rest.slice(at).trimStart();
    }
    return result.length ? result : [''];
};
export function createSpecificationsPdf(products) {
    if (!products?.length) throw new Error('No hay productos para el PDF.');
    const pages = []; let stream = '', y = 0, continuation = '';
    const text = (value, x, yy, size = 10, bold = false, color = '0.23 0.29 0.40') => {stream += `BT /${bold ? 'B' : 'F'} ${size} Tf ${color} rg 1 0 0 1 ${x} ${yy} Tm <${hex(value)}> Tj ET\n`;};
    const startPage = () => {
        if (stream) pages.push(stream);
        stream = '0.96 0.98 0.99 rg 0 754 595 88 re f\n0.08 0.42 0.44 rg 40 752 515 2 re f\n';
        text('Especificaciones del Producto', 40, 802, 18, true, '0.08 0.42 0.44');
        text('Solicitud de cotización', 40, 765, 9);
        y = 727;
        if (continuation) {
            for (const part of wrap(`Continuación: ${continuation}`, 43)) {text(part, 40, y, 11, true); y -= 15;}
            y -= 14;
        }
    };
    const space = height => {if (y - height < 52) startPage();};
    const row = (heading, value) => {
        // Stack label and value to preserve long labels, values and URLs without clipping.
        const labels = wrap(heading, 51), values = wrap(value, 48);
        space(16 + Math.min(values.length, 3) * 13 + labels.length * 12);
        for (const part of labels) {space(13); text(part, 50, y, 9, true, '0.08 0.42 0.44'); y -= 12;}
        for (const part of values) {space(14); text(part, 50, y, 10); y -= 13;}
        y -= 9;
    };
    startPage();
    products.forEach((product, index) => {
        const names = wrap(`${index + 1}. ${product.name}`, 37);
        continuation = '';
        const rows = [['Categoría',product.category],['Cantidad solicitada',`${product.quantity} ${product.unit}`],...(product.type ? [['Tipo de producto',product.type]] : []),...(product.specifications.length ? product.specifications : [['Especificaciones','Pendientes de confirmar']])];
        const height = names.length * 17 + 37 + rows.reduce((sum,[heading,value]) => sum + wrap(heading,51).length * 12 + wrap(value,48).length * 13 + 9,0);
        space(Math.min(height, 620));
        stream += `0.94 0.96 0.98 rg 40 ${y - names.length * 17 - 7} 515 ${names.length * 17 + 18} re f\n`;
        names.forEach(part => {text(part, 50, y - 3, 13, true); y -= 17;}); y -= 21;
        continuation = product.name;
        row('Categoría', product.category);
        row('Cantidad solicitada', `${product.quantity} ${product.unit}`);
        if (product.type) row('Tipo de producto', product.type);
        if (!product.specifications.length) row('Especificaciones', 'Pendientes de confirmar');
        product.specifications.forEach(([key, value]) => row(key, value));
        y -= 16;
    });
    pages.push(stream);
    const objects = [], add = body => {objects.push(body); return objects.length;};
    const root = add(''), tree = add('');
    const regular = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
    const bold = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
    const pageIds = pages.map((body, i) => {
        body += `BT /F 9 Tf 0.45 0.50 0.58 rg 1 0 0 1 40 27 Tm <${hex(`Especificaciones del Producto | Página ${i + 1} de ${pages.length}`)}> Tj ET\n`;
        const content = add(`<< /Length ${body.length} >>\nstream\n${body}endstream`);
        return add(`<< /Type /Page /Parent ${tree} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F ${regular} 0 R /B ${bold} 0 R >> >> /Contents ${content} 0 R >>`);
    });
    objects[root - 1] = `<< /Type /Catalog /Pages ${tree} 0 R >>`;
    objects[tree - 1] = `<< /Type /Pages /Kids [${pageIds.map(id => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`;
    let output = '%PDF-1.4\n', offsets = [0];
    objects.forEach((body, index) => {offsets.push(output.length); output += `${index + 1} 0 obj\n${body}\nendobj\n`;});
    const xref = output.length;
    output += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n${offsets.slice(1).map(offset => `${String(offset).padStart(10, '0')} 00000 n \n`).join('')}trailer\n<< /Size ${objects.length + 1} /Root ${root} 0 R >>\nstartxref\n${xref}\n%%EOF`;
    return new TextEncoder().encode(output);
}

